import React, { useEffect, useRef } from "react";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	ButtonBase,
	Chip,
	FormControl,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	Stepper,
	Step,
	StepLabel,
	TextField,
	Typography,
} from "@material-ui/core";
import {
	CheckCircleOutline,
	ExpandMore,
	Visibility,
	VisibilityOff,
} from "@material-ui/icons";
import { Field } from "formik";
import { makeStyles } from "@material-ui/core/styles";

import QueueSelect from "../QueueSelect";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
	wizard: {
		width: "100%",
	},
	stepper: {
		padding: theme.spacing(1, 0, 2),
		background: "transparent",
		[theme.breakpoints.down("xs")]: {
			display: "none",
		},
	},
	stepPanel: {
		borderTop: `1px solid ${theme.palette.divider}`,
		paddingTop: theme.spacing(2),
	},
	stepHeading: {
		fontWeight: 700,
		marginBottom: theme.spacing(0.5),
		outline: "none",
	},
	stepHelp: {
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(2),
	},
	fieldsGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
		gap: theme.spacing(1.5, 2),
		[theme.breakpoints.down("xs")]: {
			gridTemplateColumns: "1fr",
		},
	},
	fullField: {
		gridColumn: "1 / -1",
	},
	choiceGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
		gap: theme.spacing(1.5),
		marginBottom: theme.spacing(2),
		[theme.breakpoints.down("xs")]: {
			gridTemplateColumns: "1fr",
		},
	},
	choice: {
		display: "block",
		width: "100%",
		padding: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 12,
		textAlign: "left",
		"&:hover": {
			borderColor: theme.palette.primary.main,
		},
	},
	choiceSelected: {
		borderColor: theme.palette.primary.main,
		backgroundColor: theme.palette.action.selected,
	},
	choiceTitleRow: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(0.5),
	},
	choiceTitle: {
		fontWeight: 700,
	},
	choiceHelp: {
		color: theme.palette.text.secondary,
		lineHeight: 1.5,
	},
	section: {
		marginTop: theme.spacing(2.5),
	},
	reviewGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
		gap: theme.spacing(1.5),
		[theme.breakpoints.down("xs")]: {
			gridTemplateColumns: "1fr",
		},
	},
	reviewCard: {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 12,
		padding: theme.spacing(2),
	},
	reviewLabel: {
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(0.5),
	},
	optional: {
		marginTop: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 12,
		boxShadow: "none",
		"&:before": { display: "none" },
	},
	optionalDetails: {
		display: "block",
		padding: theme.spacing(0, 2, 2),
	},
	note: {
		display: "flex",
		alignItems: "flex-start",
		gap: theme.spacing(1),
		marginTop: theme.spacing(2),
		padding: theme.spacing(1.5),
		borderRadius: 10,
		backgroundColor: theme.palette.action.hover,
	},
	noteIcon: {
		color: theme.palette.success.main,
		fontSize: 20,
		marginTop: 1,
	},
}));

const HumanUserWizard = ({
	values,
	setFieldValue,
	setFieldTouched,
	touched,
	errors,
	activeStep,
	selectedQueueIds,
	onQueueIdsChange,
	whatsappId,
	onWhatsappIdChange,
	whatsApps,
	loadingWhatsApps,
	showPassword,
	onTogglePassword,
}) => {
	const classes = useStyles();
	const wizardRef = useRef(null);
	const headingRef = useRef(null);
	const previousStepRef = useRef(activeStep);

	useEffect(() => {
		if (previousStepRef.current !== activeStep) {
			wizardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
			headingRef.current?.focus({ preventScroll: true });
			previousStepRef.current = activeStep;
		}
	}, [activeStep]);

	const profileLabel = values.profile === "admin"
		? i18n.t("userModal.humanWizard.roles.admin.title")
		: i18n.t("userModal.humanWizard.roles.agent.title");
	const queueSummary = selectedQueueIds.length
		? i18n.t("userModal.humanWizard.review.queueCount", { count: selectedQueueIds.length })
		: i18n.t("userModal.humanWizard.review.noQueues");
	const selectedWhatsapp = whatsApps.find(item => item.id === whatsappId);

	return (
		<div className={classes.wizard} ref={wizardRef} data-testid="human-user-wizard">
			<Stepper activeStep={activeStep} alternativeLabel className={classes.stepper}>
				{["access", "service", "review"].map(step => (
					<Step key={step}>
						<StepLabel>{i18n.t(`userModal.humanWizard.steps.${step}`)}</StepLabel>
					</Step>
				))}
			</Stepper>

			<div className={classes.stepPanel}>
				{activeStep === 0 && (
					<>
						<Typography ref={headingRef} tabIndex={-1} variant="subtitle1" className={classes.stepHeading}>
							{i18n.t("userModal.humanWizard.accessTitle")}
						</Typography>
						<Typography variant="body2" className={classes.stepHelp}>
							{i18n.t("userModal.humanWizard.accessHelp")}
						</Typography>
						<div className={classes.fieldsGrid}>
							<TextField
								name="name"
								value={values.name || ""}
								onChange={event => setFieldValue("name", event.target.value)}
								onBlur={() => setFieldTouched("name", true)}
								label={i18n.t("userModal.form.name")}
								autoFocus
								error={touched.name && Boolean(errors.name)}
								helperText={(touched.name && errors.name) || i18n.t("userModal.humanWizard.nameHelp")}
								variant="outlined"
								margin="dense"
								fullWidth
							/>
							<TextField
								name="email"
								value={values.email || ""}
								onChange={event => setFieldValue("email", event.target.value)}
								onBlur={() => setFieldTouched("email", true)}
								label={i18n.t("userModal.form.email")}
								error={touched.email && Boolean(errors.email)}
								helperText={(touched.email && errors.email) || i18n.t("userModal.humanWizard.emailHelp")}
								variant="outlined"
								margin="dense"
								fullWidth
							/>
							<div className={classes.fullField}>
								<TextField
									name="password"
									value={values.password || ""}
									onChange={event => setFieldValue("password", event.target.value)}
									onBlur={() => setFieldTouched("password", true)}
									label={i18n.t("userModal.form.password")}
									type={showPassword ? "text" : "password"}
									error={touched.password && Boolean(errors.password)}
									helperText={(touched.password && errors.password) || i18n.t("userModal.humanWizard.passwordHelp")}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													aria-label={i18n.t("userModal.humanWizard.togglePassword")}
													onClick={onTogglePassword}
												>
													{showPassword ? <VisibilityOff /> : <Visibility />}
												</IconButton>
											</InputAdornment>
										),
									}}
									variant="outlined"
									margin="dense"
									fullWidth
								/>
							</div>
						</div>
					</>
				)}

				{activeStep === 1 && (
					<>
						<Typography ref={headingRef} tabIndex={-1} variant="subtitle1" className={classes.stepHeading}>
							{i18n.t("userModal.humanWizard.serviceTitle")}
						</Typography>
						<Typography variant="body2" className={classes.stepHelp}>
							{i18n.t("userModal.humanWizard.serviceHelp")}
						</Typography>
						<div className={classes.choiceGrid}>
							{[
								{ value: "user", key: "agent" },
								{ value: "admin", key: "admin" },
							].map(role => (
								<ButtonBase
									key={role.value}
									className={`${classes.choice} ${values.profile === role.value ? classes.choiceSelected : ""}`}
									onClick={() => setFieldValue("profile", role.value)}
								>
									<div className={classes.choiceTitleRow}>
										<Typography className={classes.choiceTitle}>
											{i18n.t(`userModal.humanWizard.roles.${role.key}.title`)}
										</Typography>
										{values.profile === role.value && <CheckCircleOutline color="primary" />}
									</div>
									<Typography variant="body2" className={classes.choiceHelp}>
										{i18n.t(`userModal.humanWizard.roles.${role.key}.help`)}
									</Typography>
								</ButtonBase>
							))}
						</div>

						<div className={classes.section}>
							<Typography variant="subtitle2" className={classes.choiceTitle}>
								{i18n.t("userModal.humanWizard.queuesTitle")}
							</Typography>
							<Typography variant="body2" className={classes.choiceHelp}>
								{i18n.t("userModal.humanWizard.queuesHelp")}
							</Typography>
							<QueueSelect
								selectedQueueIds={selectedQueueIds}
								onChange={onQueueIdsChange}
								label={i18n.t("userModal.humanWizard.queuesLabel")}
							/>
						</div>

						{!loadingWhatsApps && (
							<FormControl variant="outlined" margin="dense" fullWidth>
								<InputLabel>{i18n.t("userModal.form.whatsapp")}</InputLabel>
								<Select
									value={whatsappId || ""}
									onChange={event => onWhatsappIdChange(event.target.value)}
									label={i18n.t("userModal.form.whatsapp")}
								>
									<MenuItem value="">{i18n.t("userModal.humanWizard.noDefaultConnection")}</MenuItem>
									{whatsApps.map(item => (
										<MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
					</>
				)}

				{activeStep === 2 && (
					<>
						<Typography ref={headingRef} tabIndex={-1} variant="subtitle1" className={classes.stepHeading}>
							{i18n.t("userModal.humanWizard.reviewTitle")}
						</Typography>
						<Typography variant="body2" className={classes.stepHelp}>
							{i18n.t("userModal.humanWizard.reviewHelp")}
						</Typography>
						<div className={classes.reviewGrid}>
							{[
								[i18n.t("userModal.humanWizard.review.person"), values.name],
								[i18n.t("userModal.humanWizard.review.access"), values.email],
								[i18n.t("userModal.humanWizard.review.role"), profileLabel],
								[i18n.t("userModal.humanWizard.review.service"), queueSummary],
							].map(([label, value]) => (
								<div className={classes.reviewCard} key={label}>
									<Typography variant="caption" className={classes.reviewLabel}>{label}</Typography>
									<Typography>{value || "—"}</Typography>
								</div>
							))}
						</div>
						{selectedWhatsapp && (
							<Chip size="small" label={`${i18n.t("userModal.form.whatsapp")}: ${selectedWhatsapp.name}`} style={{ marginTop: 12 }} />
						)}

						<Accordion className={classes.optional}>
							<AccordionSummary expandIcon={<ExpandMore />}>
								<div>
									<Typography className={classes.choiceTitle}>{i18n.t("userModal.humanWizard.preferencesTitle")}</Typography>
									<Typography variant="body2" className={classes.choiceHelp}>{i18n.t("userModal.humanWizard.preferencesHelp")}</Typography>
								</div>
							</AccordionSummary>
							<AccordionDetails className={classes.optionalDetails}>
								<div className={classes.fieldsGrid}>
									<FormControl variant="outlined" margin="dense" fullWidth>
										<InputLabel>{i18n.t("userModal.form.language")}</InputLabel>
										<Field as={Select} name="language" label={i18n.t("userModal.form.language")}>
											<MenuItem value="pt-BR">{i18n.t("signup.form.languageOptions.pt")}</MenuItem>
											<MenuItem value="en">{i18n.t("signup.form.languageOptions.en")}</MenuItem>
											<MenuItem value="es">{i18n.t("signup.form.languageOptions.es")}</MenuItem>
										</Field>
									</FormControl>
									<FormControl variant="outlined" margin="dense" fullWidth>
										<InputLabel>{i18n.t("userModal.form.ticketOrder")}</InputLabel>
										<Field as={Select} name="ticketOrder" label={i18n.t("userModal.form.ticketOrder")}>
											<MenuItem value="oldest_first">{i18n.t("userModal.form.ticketOrderOptions.oldestFirst")}</MenuItem>
											<MenuItem value="newest_first">{i18n.t("userModal.form.ticketOrderOptions.newestFirst")}</MenuItem>
										</Field>
									</FormControl>
								</div>
							</AccordionDetails>
						</Accordion>

						<div className={classes.note}>
							<CheckCircleOutline className={classes.noteIcon} />
							<Typography variant="body2">{i18n.t("userModal.humanWizard.afterCreateHelp")}</Typography>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default HumanUserWizard;
